// utils/mountDynamicComponent.js
import { defineNuxtPlugin } from '#app';
import { createApp, h } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import FloatingVue from 'floating-vue';

const floatingVueOptions = {
    themes: {
        tooltip: {
            popperTriggers: ['hover'],
            delay: {
                show: 200,
                hide: 150,
            },
        },
    },
};
import {globalMixin} from "~/plugins/global-mixin.client";
import {eventMixin} from "~/plugins/events-mixin";

/**
 * Utility to create a Nuxt plugin that dynamically mounts any component
 * @param {Object} options
 * @param {Object} options.component     - The Vue component to render
 * @param {string} options.functionName  - Name of the global method (without $)
 * @param {Array<string>} [options.provideKeys] - Keys to mirror from parent to instance
 * @returns {import('#app').NuxtPlugin}
 */
function mountDynamicComponent({
                                   component,
                                   functionName,
                                   provideKeys = [
                                       'store',
                                       'auth',
                                       'events',
                                       'notify',
                                       'api',
                                       'permissions',
                                       'isSuperAdmin',
                                       'router',
                                       'route',
                                       'set',
                                       't',
                                       'timestampDate',
                                       'timestampDateOnly',
                                       'showConfirmation',
                                       'showModal',
                                       'showOffCanvas',
                                       'showFullPagePortal',
                                       'quill',
                                   ]
                               }) {
    return defineNuxtPlugin(({ vueApp }) => {
        const instances = {};

        const removeById = id => {
            const app = instances[id];
            if (app) {
                app.unmount();
                delete instances[id];
            }
            const el = document.getElementById(id);
            if (el) el.remove();
        };

        const show = ({ parent, ...props }) => {
            const id = uuidv4();
            const container = document.createElement('div');
            container.id = id;
            document.body.appendChild(container);

            const app = createApp({
                render() {
                    return h(component, { removeById, content: {
                            ...props,
                            id
                        } });
                }
            });

            provideKeys.forEach(key => {
                const val = parent[`$${key}`] ?? parent[key];
                if (val !== undefined) {
                    app.provide(key, val);
                    app.config.globalProperties[`$${key}`] = val;
                }
            });

            app.mixin(globalMixin);
            app.mixin(eventMixin);

            app.use(FloatingVue, floatingVueOptions);
            app.directive('truncate', vueApp._context.directives.truncate);
            app.mount(container);
            instances[id] = app;
        };

        const method = `$${functionName}`;
        vueApp.config.globalProperties[method] = show;
        vueApp.provide(functionName, show);
    });
}

export default mountDynamicComponent;